import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../app/api-client';

interface RuleState {
  evaluatedState?: string | null;
}

/** An alert is a defined rule with a state (UC-ALRT-01); the active ones are the firing ones. */
export function countFiringAlerts(rules: readonly RuleState[]): number {
  return rules.filter((rule) => rule.evaluatedState === 'firing').length;
}

/**
 * Caiet §4.2, NC-MENU-18 + CF-46 — the indicator on the System Administrator's
 * «Alerte și notificări» entry counts the ACTIVE alerts, the ones the predefined
 * alert dashboard shows as firing; the unread events of the removed inbox (D7) are
 * not what the entry indicates. Same key and same read as the rules register, so the
 * two cannot disagree. The indicator links to the predefined alert dashboard
 * (ALERT_DASHBOARD_SEGMENT in the nav model).
 *
 * The count is absent, not zero, when the read fails: an unreachable alerts service
 * must not read as "nothing is firing", so the badge does not render while it is unknown.
 */
export function useFiringAlertsCount(enabled: boolean): { count: number; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () =>
      apiClient.get<{ rules: RuleState[] }>('/admin/alert-rules').then((r) => r.data.rules),
    enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
  return { count: q.data ? countFiringAlerts(q.data) : 0, isLoading: q.isLoading };
}
